// Copyright (c) 2025 Nadja Neb. All Rights Reserved.
// All rights to this code belongs to Nadja Neb. The code may not be copied, modified or redistributed except with express permission.

import './vanilla/realestate.js';
import './vanilla/chartLabels.js';
import './vanilla/monthlyBarChart.js';
import './vanilla/costDonutChart.js';
import './vanilla/ownFundsChart.js';
import './vanilla/grossYieldGauge.js';
import './vanilla/developmentWaveChart.js';
import './vanilla/scenarioTable.js';
import './delete-user';
import AutoNumeric from 'autonumeric';
import { initPagedSlider } from './vanilla/pagedSlider.js';
import { initAnalyticsTracking, trackCalculatorStarted } from './vanilla/analyticsTracker.js';
import { initCookieConsent } from './vanilla/cookieConsent.js';
import { initInvestmentFunnelHandoff } from './vanilla/investmentFunnelHandoff.js';
import { initMoneyInputs, moneyInputValue } from './vanilla/moneyInputs.js';
import { initWidgetConfigurators } from './vanilla/widgetConfigurator.js';

const CASHFLOW_PWA_VISIT_KEY = 'calcimoCashflowPwaVisit';
const CASHFLOW_PWA_PENDING_KEY = 'calcimoCashflowPwaPending';
const CASHFLOW_PWA_HANDLED_KEY = 'calcimoCashflowPwaHandledVisit';

let deferredPwaInstallPrompt = null;

const isStandalonePwa = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const registerServiceWorker = () => {
    if (!('serviceWorker' in navigator) || !window.isSecureContext) {
        return;
    }

    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js').catch(() => {
            // Ignore failed registration so the app keeps working normally online.
        });
    });
};

const dispatchPwaStateEvent = (name) => {
    document.dispatchEvent(new CustomEvent(name));
};

window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPwaInstallPrompt = event;
    dispatchPwaStateEvent('calcimo:pwa-install-available');
});

window.addEventListener('appinstalled', () => {
    deferredPwaInstallPrompt = null;
    dispatchPwaStateEvent('calcimo:pwa-installed');
});

registerServiceWorker();

const mobileSidebarToggle = () => {
    const html = document.documentElement;
    const sidebarToggle = document.querySelector('.navbar-toggle');

    if (!sidebarToggle) {
        return;
    }

    const closeSidebar = () => {
        html.classList.remove('nav-open');
        sidebarToggle.classList.remove('toggled');
        document.querySelectorAll('.bodyClick').forEach((element) => element.remove());
    };

    const openSidebar = () => {
        html.classList.add('nav-open');
        sidebarToggle.classList.add('toggled');

        if (!document.querySelector('.bodyClick')) {
            const bodyClick = document.createElement('div');
            bodyClick.className = 'bodyClick';
            bodyClick.addEventListener('click', closeSidebar);
            document.body.appendChild(bodyClick);
        }
    };

    sidebarToggle.addEventListener('click', (event) => {
        event.preventDefault();

        if (window.innerWidth > 991) {
            return;
        }

        if (html.classList.contains('nav-open')) {
            closeSidebar();
            return;
        }

        openSidebar();
    });

    window.addEventListener('resize', () => {
        if (window.innerWidth > 991) {
            closeSidebar();
        }
    });
};

const professionalSettingsToggle = () => {
    const panel = document.querySelector('#professional-settings');
    const toggle = document.querySelector('[data-professional-settings-toggle]');
    const close = document.querySelector('[data-professional-settings-close]');

    if (!panel || !toggle) {
        return;
    }

    const setOpenState = (isOpen) => {
        panel.classList.toggle('is-open', isOpen);
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    toggle.addEventListener('click', (event) => {
        event.preventDefault();
        setOpenState(!panel.classList.contains('is-open'));
    });

    close?.addEventListener('click', () => setOpenState(false));
};

const ownerEquityCardToggle = () => {
    const cards = document.querySelectorAll('[data-owner-equity-card]');
    if (cards.length === 0) {
        return;
    }

    cards.forEach((card) => {
        const prompt = card.querySelector('[data-owner-equity-prompt]');
        const form = card.querySelector('[data-owner-equity-form]');
        const startButton = card.querySelector('[data-owner-equity-start]');

        if (!prompt || !form || !startButton) {
            return;
        }

        startButton.addEventListener('click', () => {
            prompt.hidden = true;
            form.hidden = false;
            form.querySelector('input[name="scenarioJump"]')?.focus();
        });
    });
};

const familyTransferInfoToggle = () => {
    const toggle = document.querySelector('[data-family-transfer-info-toggle]');
    const panel = document.querySelector('#family-transfer-tax-info');

    if (!toggle || !panel) {
        return;
    }

    const setOpen = (isOpen) => {
        panel.classList.toggle('is-open', isOpen);
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    toggle.addEventListener('click', () => setOpen(!panel.classList.contains('is-open')));
    panel.querySelector('[data-family-transfer-info-close]')?.addEventListener('click', () => {
        setOpen(false);
        toggle.focus();
    });
    document.addEventListener('click', (event) => {
        if (panel.classList.contains('is-open') && !panel.contains(event.target) && !toggle.contains(event.target)) {
            setOpen(false);
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
            setOpen(false);
            toggle.focus();
        }
    });
};

const initInfoDetails = () => {
    document.addEventListener('click', (event) => {
        const detail = event.target.closest('.simple-calculator__info');
        const closeButton = event.target.closest('[data-info-close]');

        document.querySelectorAll('.simple-calculator__info[open]').forEach((openDetail) => {
            if (openDetail !== detail || closeButton) {
                openDetail.open = false;
            }
        });

        if (closeButton) {
            detail?.querySelector('summary')?.focus();
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        document.querySelectorAll('.simple-calculator__info[open]').forEach((detail) => {
            detail.open = false;
            detail.querySelector('summary')?.focus();
        });
    });
};

const initTooltips = () => {
    if (!window.jQuery || !window.jQuery.fn.tooltip) {
        return;
    }

    const template = '<div class="tooltip calcimo-info-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div><button type="button" class="calcimo-info-tooltip__close" aria-label="">×</button></div>';
    let activeTrigger = null;

    const hideActive = (restoreFocus = false) => {
        if (!activeTrigger) {
            return;
        }

        window.jQuery(activeTrigger).tooltip('hide');
        if (restoreFocus) {
            activeTrigger.focus();
        }
        activeTrigger = null;
    };

    document.addEventListener('click', (event) => {
        if (event.target.closest('.calcimo-info-tooltip__close')) {
            hideActive(true);
            return;
        }

        const trigger = event.target.closest('[data-toggle="tooltip"]');
        if (trigger) {
            if (activeTrigger === trigger) {
                hideActive();
                return;
            }

            hideActive();
            window.jQuery(trigger).tooltip({ trigger: 'manual', container: 'body', template });
            window.jQuery(trigger).tooltip('show');
            activeTrigger = trigger;
            const tooltipId = trigger.getAttribute('aria-describedby');
            document.getElementById(tooltipId)?.querySelector('.calcimo-info-tooltip__close')
                ?.setAttribute('aria-label', document.body.dataset.infoCloseLabel);
            return;
        }

        if (!event.target.closest('.calcimo-info-tooltip')) {
            hideActive();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            hideActive(true);
        }
    });
};

const scrollToCalculationResults = () => {
    if (window.location.hash !== '#calculation-results') {
        return;
    }

    const resultsAnchor = document.querySelector('#calculation-results');
    if (!resultsAnchor) {
        return;
    }

    window.setTimeout(() => {
        resultsAnchor.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 120);
};

const initLogoutTrigger = () => {
    const logoutTriggers = document.querySelectorAll('[data-logout-trigger]');
    const logoutForm = document.querySelector('#logout-form');

    if (logoutTriggers.length === 0 || !logoutForm) {
        return;
    }

    logoutTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            logoutForm.submit();
        });
    });
};

const initMobileNavSubmenus = () => {
    const toggles = document.querySelectorAll('[data-mobile-nav-toggle]');
    if (toggles.length === 0) {
        return;
    }

    const closePanel = (toggle, panel, item) => {
        toggle.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-open');
        panel.style.maxHeight = `${panel.scrollHeight}px`;

        window.requestAnimationFrame(() => {
            panel.style.maxHeight = '0px';
            panel.style.opacity = '0';
        });

        window.setTimeout(() => {
            panel.hidden = true;
            panel.style.maxHeight = '';
            panel.style.opacity = '';
        }, 220);
    };

    const openPanel = (toggle, panel, item) => {
        toggle.setAttribute('aria-expanded', 'true');
        item.classList.add('is-open');
        panel.hidden = false;
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';

        window.requestAnimationFrame(() => {
            panel.style.maxHeight = `${panel.scrollHeight}px`;
            panel.style.opacity = '1';
        });

        window.setTimeout(() => {
            panel.style.maxHeight = '';
            panel.style.opacity = '';
        }, 220);
    };

    toggles.forEach((toggle) => {
        const targetId = toggle.getAttribute('data-target');
        const item = toggle.closest('.calcimo-mobile-nav__item--submenu');
        const panel = targetId ? document.getElementById(targetId) : null;

        if (!targetId || !item || !panel) {
            return;
        }

        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';

            toggles.forEach((otherToggle) => {
                if (otherToggle === toggle) {
                    return;
                }

                const otherTargetId = otherToggle.getAttribute('data-target');
                const otherItem = otherToggle.closest('.calcimo-mobile-nav__item--submenu');
                const otherPanel = otherTargetId ? document.getElementById(otherTargetId) : null;

                if (!otherItem || !otherPanel || otherToggle.getAttribute('aria-expanded') !== 'true') {
                    return;
                }

                closePanel(otherToggle, otherPanel, otherItem);
            });

            if (isOpen) {
                closePanel(toggle, panel, item);
                return;
            }

            openPanel(toggle, panel, item);
        });
    });
};

const initDonationCta = () => {
    const donationCtas = document.querySelectorAll('[data-donation-cta]');
    if (donationCtas.length === 0) {
        return;
    }

    donationCtas.forEach((donationCta) => {
        const customAmountInput = donationCta.querySelector('[data-donation-custom-amount]');
        const customAmountSubmit = donationCta.querySelector('[data-donation-custom-submit]');
        const paypalBase = donationCta.getAttribute('data-paypal-base');

        if (!customAmountInput || !customAmountSubmit || !paypalBase) {
            return;
        }

        const openDonationUrl = () => {
            const normalizedAmount = Number.parseInt(customAmountInput.value, 10);
            const targetUrl = Number.isFinite(normalizedAmount) && normalizedAmount > 0
                ? `${paypalBase}/${normalizedAmount}`
                : paypalBase;

            window.open(targetUrl, '_blank', 'noopener,noreferrer');
        };

        customAmountSubmit.addEventListener('click', openDonationUrl);

        customAmountInput.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') {
                return;
            }

            event.preventDefault();
            customAmountSubmit.click();
        });
    });
};

const initCashflowPwaPrompt = () => {
    const cashflowForm = document.querySelector('.cashflow__form');
    const prompt = document.querySelector('[data-cashflow-pwa-prompt]');
    if (!cashflowForm || !prompt) {
        return;
    }

    const currentPath = `${window.location.pathname}${window.location.search}`;

    const parseReferrerPath = () => {
        if (!document.referrer) {
            return null;
        }

        try {
            const referrerUrl = new URL(document.referrer);

            if (referrerUrl.origin !== window.location.origin) {
                return null;
            }

            return `${referrerUrl.pathname}${referrerUrl.search}`;
        } catch (error) {
            return null;
        }
    };

    const clearPromptState = () => {
        sessionStorage.removeItem(CASHFLOW_PWA_PENDING_KEY);
        sessionStorage.removeItem(CASHFLOW_PWA_HANDLED_KEY);
    };

    if (isStandalonePwa()) {
        clearPromptState();
        return;
    }

    const referrerPath = parseReferrerPath();
    let visitId = sessionStorage.getItem(CASHFLOW_PWA_VISIT_KEY);

    if (!visitId || referrerPath !== currentPath) {
        visitId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        sessionStorage.setItem(CASHFLOW_PWA_VISIT_KEY, visitId);
        clearPromptState();
    }

    const installButton = prompt.querySelector('[data-cashflow-pwa-install]');
    const instruction = prompt.querySelector('[data-cashflow-pwa-instruction]');
    const closeButtons = prompt.querySelectorAll('[data-cashflow-pwa-close]');
    const iosDevice = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const androidDevice = /android/i.test(window.navigator.userAgent);

    const getInstructionText = () => {
        if (!instruction) {
            return '';
        }

        if (iosDevice) {
            return instruction.dataset.ios || instruction.dataset.default || '';
        }

        if (androidDevice) {
            return instruction.dataset.android || instruction.dataset.default || '';
        }

        return instruction.dataset.desktop || instruction.dataset.default || '';
    };

    const syncPromptUi = () => {
        if (instruction) {
            instruction.textContent = getInstructionText();
        }

        if (installButton) {
            installButton.hidden = !deferredPwaInstallPrompt;
        }
    };

    const closePrompt = () => {
        prompt.hidden = true;
        prompt.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cashflow-pwa-prompt-open');
    };

    const openPrompt = () => {
        if (isStandalonePwa()) {
            clearPromptState();
            return;
        }

        syncPromptUi();
        prompt.hidden = false;
        prompt.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cashflow-pwa-prompt-open');

        if (installButton && !installButton.hidden) {
            installButton.focus();
            return;
        }

        prompt.querySelector('[data-cashflow-pwa-close]')?.focus();
    };

    closeButtons.forEach((button) => {
        button.addEventListener('click', closePrompt);
    });

    prompt.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || !target.hasAttribute('data-cashflow-pwa-backdrop')) {
            return;
        }

        closePrompt();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || prompt.hidden) {
            return;
        }

        closePrompt();
    });

    document.addEventListener('calcimo:pwa-install-available', syncPromptUi);
    document.addEventListener('calcimo:pwa-installed', () => {
        closePrompt();
        clearPromptState();
    });

    installButton?.addEventListener('click', async () => {
        if (!deferredPwaInstallPrompt) {
            return;
        }

        await deferredPwaInstallPrompt.prompt();
        const installChoice = await deferredPwaInstallPrompt.userChoice;

        deferredPwaInstallPrompt = null;
        syncPromptUi();

        if (installChoice?.outcome === 'accepted') {
            closePrompt();
        }
    });

    cashflowForm.addEventListener('submit', () => {
        if (isStandalonePwa() || sessionStorage.getItem(CASHFLOW_PWA_HANDLED_KEY) === visitId) {
            return;
        }

        sessionStorage.setItem(CASHFLOW_PWA_PENDING_KEY, JSON.stringify({
            visitId,
            path: currentPath,
            submittedAt: Date.now(),
        }));
    });

    const pendingPromptRaw = sessionStorage.getItem(CASHFLOW_PWA_PENDING_KEY);
    if (!pendingPromptRaw) {
        return;
    }

    const pendingPrompt = JSON.parse(pendingPromptRaw);
    if (pendingPrompt.visitId !== visitId || pendingPrompt.path !== currentPath) {
        sessionStorage.removeItem(CASHFLOW_PWA_PENDING_KEY);
        return;
    }

    if (window.location.hash !== '#calculation-results') {
        sessionStorage.removeItem(CASHFLOW_PWA_PENDING_KEY);
        return;
    }

    sessionStorage.setItem(CASHFLOW_PWA_HANDLED_KEY, visitId);
    sessionStorage.removeItem(CASHFLOW_PWA_PENDING_KEY);

    const delay = Math.max(0, 10000 - (Date.now() - pendingPrompt.submittedAt));
    window.setTimeout(openPrompt, delay);
};

const guestAuthCtaPulse = () => {
    const items = document.querySelectorAll('.guest-auth-cta__item');
    if (items.length === 0) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    let activeItem = null;

    const clearHighlight = () => {
        if (!activeItem) {
            return;
        }

        activeItem.classList.remove('is-highlighted');
        activeItem = null;
    };

    const pulseRandomItem = () => {
        const availableItems = Array.from(items).filter((item) => !item.matches(':hover'));
        if (availableItems.length === 0) {
            return;
        }

        clearHighlight();

        activeItem = availableItems[Math.floor(Math.random() * availableItems.length)];
        activeItem.classList.add('is-highlighted');

        window.setTimeout(() => {
            if (activeItem?.matches(':hover')) {
                return;
            }

            clearHighlight();
        }, 1400);
    };

    items.forEach((item) => {
        item.addEventListener('mouseenter', () => {
            if (item === activeItem) {
                clearHighlight();
            }
        });

        item.addEventListener('mouseleave', () => {
            item.classList.remove('is-highlighted');
        });
    });

    window.setTimeout(pulseRandomItem, 1800);
    window.setInterval(pulseRandomItem, 5200);
};

const initKnowledgePostForm = () => {
    const tabRoot = document.querySelector('[data-knowledge-tabs]');
    const aiGenerator = document.querySelector('[data-knowledge-ai-generator]');

    if (!tabRoot && !aiGenerator) {
        return;
    }

    const activateTab = (tabName) => {
        document.querySelectorAll('[data-knowledge-tab]').forEach((tab) => {
            tab.classList.toggle('is-active', tab.dataset.knowledgeTab === tabName);
        });

        document.querySelectorAll('[data-knowledge-tab-panel]').forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.knowledgeTabPanel === tabName);
        });
    };

    tabRoot?.querySelectorAll('[data-knowledge-tab]').forEach((tab) => {
        tab.addEventListener('click', () => activateTab(tab.dataset.knowledgeTab));
    });

    if (!aiGenerator) {
        return;
    }

    const generateEndpoint = aiGenerator.dataset.generateEndpoint;
    const translateEndpoint = aiGenerator.dataset.translateEndpoint;
    const notes = aiGenerator.querySelector('[data-knowledge-ai-notes]');
    const submit = aiGenerator.querySelector('[data-knowledge-ai-submit]');
    const translate = document.querySelector('[data-knowledge-ai-translate]');
    const statusNodes = document.querySelectorAll('[data-knowledge-ai-status]');
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const setStatus = (message, isError = false) => {
        if (statusNodes.length === 0) {
            return;
        }

        statusNodes.forEach((statusNode) => {
            statusNode.textContent = message;
            statusNode.classList.toggle('is-error', isError);
        });
    };

    const fillGeneratedFields = (fields) => {
        Object.entries(fields).forEach(([name, value]) => {
            const field = document.querySelector(`[name="${name}"]`);
            if (!field) {
                return;
            }

            field.value = value ?? '';
            field.dispatchEvent(new Event('input', {bubbles: true}));
        });
    };

    const getGermanSourceFields = () => ({
        title_de: document.querySelector('[name="title_de"]')?.value?.trim() || '',
        h1_de: document.querySelector('[name="h1_de"]')?.value?.trim() || '',
        category_de: document.querySelector('[name="category_de"]')?.value?.trim() || '',
        meta_title_de: document.querySelector('[name="meta_title_de"]')?.value?.trim() || '',
        meta_description_de: document.querySelector('[name="meta_description_de"]')?.value?.trim() || '',
        content_de: document.querySelector('[name="content_de"]')?.value?.trim() || '',
    });

    const postAiRequest = async (endpoint, body) => {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify(body),
        });

        const payload = await response.json();

        if (!response.ok) {
            throw new Error(payload.message || aiGenerator.dataset.error || 'Generation failed.');
        }

        return payload;
    };

    submit?.addEventListener('click', async () => {
        if (!generateEndpoint || !notes || !csrfToken) {
            setStatus(aiGenerator.dataset.errorConfig || 'Configuration missing.', true);
            return;
        }

        submit.disabled = true;
        translate && (translate.disabled = true);
        setStatus(aiGenerator.dataset.loading || 'Generating...');

        try {
            const payload = await postAiRequest(generateEndpoint, {notes: notes.value});
            fillGeneratedFields(payload.fields || {});
            setStatus(aiGenerator.dataset.success || 'Generated.');
            activateTab('manual');
        } catch (error) {
            setStatus(error.message, true);
        } finally {
            submit.disabled = false;
            translate && (translate.disabled = false);
        }
    });

    translate?.addEventListener('click', async () => {
        if (!translateEndpoint || !csrfToken) {
            setStatus(aiGenerator.dataset.errorConfig || 'Configuration missing.', true);
            return;
        }

        const germanSourceFields = getGermanSourceFields();

        if (!germanSourceFields.title_de || !germanSourceFields.h1_de || !germanSourceFields.content_de) {
            setStatus(aiGenerator.dataset.translateMissing || 'Please fill the required German fields first.', true);
            activateTab('manual');
            return;
        }

        submit && (submit.disabled = true);
        translate.disabled = true;
        setStatus(aiGenerator.dataset.translateLoading || 'Translating...');

        try {
            const payload = await postAiRequest(translateEndpoint, germanSourceFields);
            fillGeneratedFields(payload.fields || {});
            setStatus(aiGenerator.dataset.translateSuccess || 'Translated.');
            activateTab('manual');
        } catch (error) {
            setStatus(error.message, true);
        } finally {
            submit && (submit.disabled = false);
            translate.disabled = false;
        }
    });
};

const initKnowledgeMarkdownEditors = () => {
    const textareas = document.querySelectorAll('[data-markdown-editor]');

    if (textareas.length === 0) {
        return;
    }

    // Loaded on demand so the editor code only ships to the CMS form.
    import('./vanilla/markdownEditor.js').then(({ initMarkdownEditors }) => initMarkdownEditors(textareas));
};

const initKnowledgeImageUpload = () => {
    const root = document.querySelector('[data-knowledge-images]');
    const input = root?.querySelector('[data-knowledge-image-input]');

    if (!root || !input) {
        return;
    }

    const dropZone = root.querySelector('[data-knowledge-image-dropzone]');
    const preview = root.querySelector('[data-knowledge-image-preview]');
    const previewList = root.querySelector('[data-knowledge-image-preview-list]');
    const limitNotice = root.querySelector('[data-knowledge-image-limit]');
    const removeToggles = Array.from(root.querySelectorAll('[data-knowledge-image-remove]'));
    const maxImages = Number(root.dataset.maxImages || 3);
    let objectUrls = [];

    const render = () => {
        const files = Array.from(input.files || []);

        objectUrls.forEach((url) => URL.revokeObjectURL(url));
        objectUrls = [];

        const items = files.map((file) => {
            const item = document.createElement('li');
            item.className = 'knowledge-admin__upload-item';

            if (file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                const thumb = document.createElement('img');
                objectUrls.push(url);
                thumb.className = 'knowledge-admin__upload-item-thumb';
                thumb.src = url;
                thumb.alt = '';
                item.append(thumb);
            }

            const name = document.createElement('span');
            name.className = 'knowledge-admin__upload-item-name';
            name.textContent = file.name;
            item.append(name);

            return item;
        });

        previewList?.replaceChildren(...items);
        preview && (preview.hidden = files.length === 0);

        const keptImages = removeToggles.filter((toggle) => !toggle.checked).length;
        limitNotice && (limitNotice.hidden = keptImages + files.length <= maxImages);
    };

    input.addEventListener('change', render);
    removeToggles.forEach((toggle) => toggle.addEventListener('change', render));

    if (!dropZone) {
        return;
    }

    ['dragenter', 'dragover'].forEach((type) => {
        dropZone.addEventListener(type, (event) => {
            event.preventDefault();
            dropZone.classList.add('is-dragover');
        });
    });

    ['dragleave', 'drop'].forEach((type) => {
        dropZone.addEventListener(type, (event) => {
            event.preventDefault();
            dropZone.classList.remove('is-dragover');
        });
    });

    dropZone.addEventListener('drop', (event) => {
        const images = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith('image/'));

        if (images.length === 0) {
            return;
        }

        const transfer = new DataTransfer();
        images.forEach((file) => transfer.items.add(file));
        input.files = transfer.files;
        render();
    });
};

const initKnowledgeTeaserToggles = () => {
    document.querySelectorAll('[data-knowledge-teaser]').forEach((item) => {
        const toggle = item.querySelector('[data-knowledge-teaser-toggle]');
        const fields = item.querySelector('[data-knowledge-teaser-fields]');

        if (!toggle || !fields) {
            return;
        }

        const sync = () => {
            fields.hidden = !toggle.checked && !fields.querySelector('.is-invalid');
            item.classList.toggle('is-enabled', toggle.checked);
        };

        toggle.addEventListener('change', sync);
        sync();
    });
};

const initKnowledgeSearch = () => {
    const searchForm = document.querySelector('[data-knowledge-search]');
    const searchInput = document.querySelector('[data-knowledge-search-input]');
    const clearButton = document.querySelector('[data-knowledge-search-clear]');

    if (!searchForm || !searchInput || !clearButton) {
        return;
    }

    const syncClearVisibility = () => {
        clearButton.classList.toggle('is-hidden', searchInput.value.trim() === '');
    };

    clearButton.addEventListener('click', () => {
        searchInput.value = '';
        syncClearVisibility();
        searchInput.focus();
        searchForm.submit();
    });

    searchInput.addEventListener('input', syncClearVisibility);
    syncClearVisibility();
    initKnowledgeCategorySuggestions(searchInput);
};

const initKnowledgeCategorySuggestions = (searchInput) => {
    const panel = document.querySelector('[data-knowledge-suggestions]');

    if (!panel) {
        return;
    }

    const options = Array.from(panel.querySelectorAll('[data-knowledge-suggestion]'));
    let activeIndex = -1;

    const visibleOptions = () => options.filter(option => !option.hidden);

    const setActive = (index) => {
        const visible = visibleOptions();
        activeIndex = index >= 0 && index < visible.length ? index : -1;

        options.forEach(option => {
            option.classList.remove('is-active');
            option.setAttribute('aria-selected', 'false');
        });

        const activeOption = visible[activeIndex];

        if (!activeOption) {
            searchInput.removeAttribute('aria-activedescendant');
            return;
        }

        activeOption.classList.add('is-active');
        activeOption.setAttribute('aria-selected', 'true');
        activeOption.scrollIntoView({ block: 'nearest' });
        searchInput.setAttribute('aria-activedescendant', activeOption.id);
    };

    const filterOptions = () => {
        const term = searchInput.value.trim().toLocaleLowerCase();

        options.forEach(option => {
            option.hidden = term !== '' && !option.dataset.label.toLocaleLowerCase().includes(term);
        });
    };

    const open = () => {
        filterOptions();
        const hasOptions = visibleOptions().length > 0;
        panel.hidden = !hasOptions;
        searchInput.setAttribute('aria-expanded', hasOptions ? 'true' : 'false');
        setActive(-1);
    };

    const close = () => {
        panel.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
        searchInput.removeAttribute('aria-activedescendant');
        activeIndex = -1;
    };

    const select = (option) => {
        if (option?.dataset.url) {
            window.location.href = option.dataset.url;
        }
    };

    searchInput.addEventListener('focus', open);
    searchInput.addEventListener('click', () => {
        if (panel.hidden) {
            open();
        }
    });
    searchInput.addEventListener('input', open);
    searchInput.addEventListener('blur', close);

    searchInput.addEventListener('keydown', (event) => {
        const isOpen = !panel.hidden;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();

            if (!isOpen) {
                open();
            }

            const count = visibleOptions().length;

            if (event.key === 'ArrowDown') {
                setActive(activeIndex + 1 < count ? activeIndex + 1 : 0);
            } else {
                setActive(activeIndex > 0 ? activeIndex - 1 : count - 1);
            }

            return;
        }

        if (!isOpen) {
            return;
        }

        if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            select(visibleOptions()[activeIndex]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
        } else if (event.key === 'Tab') {
            close();
        }
    });

    options.forEach(option => {
        // Keep the input focused so the panel does not close before the click is handled.
        option.addEventListener('mousedown', event => event.preventDefault());
        option.addEventListener('click', () => select(option));
        option.addEventListener('mousemove', () => {
            const index = visibleOptions().indexOf(option);

            if (index !== activeIndex) {
                setActive(index);
            }
        });
    });
};

const showShareToast = (message, anchorEl = null) => {
    document.querySelectorAll('.share-toast').forEach(el => el.remove());

    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `<i class="fas fa-check" aria-hidden="true"></i><span>${message}</span>`;
    document.body.appendChild(toast);

    if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        toast.classList.add('share-toast--anchored');
        toast.style.setProperty('--toast-anchor-left', `${rect.left + rect.width / 2 + window.scrollX}px`);
        toast.style.setProperty('--toast-anchor-top',  `${rect.top + window.scrollY - 12}px`);
    }

    requestAnimationFrame(() => {
        requestAnimationFrame(() => toast.classList.add('share-toast--visible'));
    });

    setTimeout(() => {
        toast.classList.remove('share-toast--visible');
        setTimeout(() => toast.remove(), 300);
    }, 2500);
};

const initShareButton = () => {
    const shareButtons = document.querySelectorAll('[data-cashflow-share]');
    if (shareButtons.length === 0) return;
    const shareBtn = shareButtons[0];

    const form = document.querySelector('.cashflow__form');
    if (!form) return;

    const field    = (name) => form.querySelector(`[name="${name}"]`);
    const checkbox = () => form.querySelector('[name="familyTransferTaxExempt"]:not([type="hidden"])');

    const readFormData = () => {
        const data = {
            usageType:               shareBtn.dataset.usageType || 'capitalInvestment',
            purchasePrice:           field('purchasePrice')?.value || '',
            netRent:                 field('netRent')?.value       || '',
            houseFee:                field('houseFee')?.value      || '',
            houseFeeMode:            field('houseFeeMode')?.value  || 'houseFee',
            maintenanceArea:         field('maintenanceArea')?.value || '',
            maintenanceCondition:    field('maintenanceCondition')?.value || '',
            broker:                  field('broker')?.value        || '0',
            financing:               field('financing')?.value     || '100',
            interestRate:            field('interestRate')?.value  || '',
            amortization:            field('amortization')?.value  || '',
            familyTransferTaxExempt: checkbox()?.checked ? '1' : '0',
        };
        ['brokerRate', 'notaryRate', 'landRegistryRate', 'transferTaxRate'].forEach(name => {
            const el = field(name);
            if (el && el.value !== '') data[name] = el.value;
        });
        return data;
    };

    // Kurz-URL-Cache: wird im Hintergrund bei Formänderung befüllt
    let cachedShortUrl = null;
    let pendingRequest = null;
    let debounceTimer  = null;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content ?? '';

    const generateShortUrl = () => {
        clearTimeout(debounceTimer);
        cachedShortUrl = null;

        debounceTimer = setTimeout(async () => {
            if (pendingRequest) pendingRequest.abort();
            const controller = new AbortController();
            pendingRequest = controller;

            try {
                const res = await fetch('/share/cashflow', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify(readFormData()),
                    signal: controller.signal,
                });
                if (res.ok) {
                    const json = await res.json();
                    cachedShortUrl = json.url ?? null;
                }
            } catch (err) {
                if (err.name !== 'AbortError') cachedShortUrl = null;
            } finally {
                pendingRequest = null;
            }
        }, 800);
    };

    // Formänderungen beobachten
    form.addEventListener('input', generateShortUrl);
    form.addEventListener('change', generateShortUrl);
    generateShortUrl(); // initial beim Laden

    const isTouchDevice = () =>
        navigator.maxTouchPoints > 0 && window.matchMedia('(pointer: coarse)').matches;

    shareButtons.forEach((button) => button.addEventListener('click', async () => {
        const label      = button.dataset.copiedLabel || 'Link copied!';
        const shareTitle = button.dataset.shareTitle  || 'Immobilien-Kalkulation';
        const shareUrl   = cachedShortUrl ?? `${window.location.origin}${window.location.pathname}`;

        const shareData = { title: shareTitle, url: shareUrl };
        if (isTouchDevice() && navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
            try {
                await navigator.share(shareData);
                return;
            } catch (err) {
                if (err.name === 'AbortError') return;
            }
        }

        // Fallback: Link in Zwischenablage kopieren
        const copyToClipboard = () => {
            if (navigator.clipboard?.writeText) {
                return navigator.clipboard.writeText(shareUrl);
            }
            return new Promise((resolve, reject) => {
                const ta = document.createElement('textarea');
                ta.value = shareUrl;
                ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                const ok = document.execCommand('copy');
                document.body.removeChild(ta);
                ok ? resolve() : reject(new Error('copy failed'));
            });
        };

        copyToClipboard()
            .then(() => {
                const originalHTML = button.innerHTML;
                const originalClass = button.className;
                button.innerHTML = `<i class="fas fa-check" aria-hidden="true"></i> ${label}`;
                button.className = originalClass.replace('btn-outline-primary', 'btn-primary');
                button.disabled = true;
                setTimeout(() => {
                    button.innerHTML = originalHTML;
                    button.className = originalClass;
                    button.disabled = false;
                }, 2500);
            })
            .catch(() => window.prompt('Link zum Teilen:', shareUrl));
    }));
};

const initKnowledgeSliders = () => {
    document.querySelectorAll('[data-knowledge-slider]').forEach((root) => {
        initPagedSlider(root, {
            viewportSelector: '[data-knowledge-viewport]',
            trackSelector: '[data-knowledge-track]',
            controlsSelector: '[data-knowledge-controls]',
            prevSelector: '[data-knowledge-prev]',
            nextSelector: '[data-knowledge-next]',
            dotsSelector: '[data-knowledge-dots]',
            slideSelector: '.knowledge-slider__slide',
            dotClass: 'knowledge-slider__dot',
            dotLabel: (index) => `${root.dataset.knowledgePageLabel} ${index + 1}`,
            visibleSlides: (width) => {
                if (width >= 1200) return 3;
                if (width >= 768) return 2;
                return 1;
            },
            hideControlsAtWidth: 1200,
        });
    });
};

const initFinancingEmbedCopy = () => {
    const button = document.querySelector('[data-financing-embed-copy]');
    const code = document.querySelector('#financingEmbedCode');
    if (!button || !code) return;

    button.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(code.value);
            button.textContent = button.dataset.copiedLabel;
        } catch {
            code.select();
        }
    });
};

const initKfwComparison = () => {
    const form = document.querySelector('[data-kfw-comparison-form]');
    const guestProgram = document.querySelector('[data-kfw-guest-program]');
    if (!form && !guestProgram) return;

    if (guestProgram) {
        guestProgram.addEventListener('change', () => {
            document.querySelector('[data-kfw-program-title]').textContent = guestProgram.selectedOptions[0].textContent;
            document.querySelectorAll('[data-kfw-program-panel], [data-kfw-program-note]').forEach((panel) => {
                panel.hidden = panel.dataset.kfwProgramPanel !== guestProgram.value
                    && panel.dataset.kfwProgramNote !== guestProgram.value;
            });
        });

        return;
    }

    initMoneyInputs(form);

    const programInput = form.querySelector('[data-kfw-program]');
    const childrenInput = form.querySelector('[data-kfw-children]');
    const qngInput = form.querySelector('[data-kfw-qng]');
    const amountInput = form.querySelector('[data-kfw-amount]');
    const interestInput = form.querySelector('#kfwInterestRate');
    const termInput = form.querySelector('[data-kfw-term]');
    const graceInput = form.querySelector('[data-kfw-grace]');
    const useEquityInput = form.querySelector('[data-kfw-use-equity]');
    const equityGroup = form.querySelector('[data-kfw-equity-group]');
    const equityLabel = form.querySelector('[data-kfw-equity-label]');
    const title = form.querySelector('[data-kfw-program-title]');
    const limitText = form.querySelector('[data-kfw-amount-limit]');
    const loanAmountInput = form.querySelector('[data-kfw-loan-amount]');
    const results = document.querySelector('[data-kfw-results]');
    if (results) {
        const originalPosition = document.createComment('KfW results position');
        results.before(originalPosition);
        const submitButton = form.querySelector('[type="submit"]');
        const mobileLayout = window.matchMedia('(max-width: 767.98px)');
        const placeResults = () => {
            if (mobileLayout.matches) {
                submitButton.after(results);
            } else {
                originalPosition.after(results);
            }
        };
        mobileLayout.addEventListener('change', placeResults);
        placeResults();
        if (window.location.hash === '#kfw-results') {
            requestAnimationFrame(() => results.scrollIntoView({ block: 'start' }));
        }
    }
    const loanAmount = () => (loanAmountInput ? moneyInputValue(loanAmountInput) : Math.max(0, Number(form.dataset.baseLoanAmount) - (useEquityInput?.checked ? Number(form.dataset.equityAmount) : 0))) || 0;
    const purchasePrice = () => Number(form.dataset.purchasePrice) || loanAmount();
    const currencyNumber = new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 0 });

    const maximumAmount = () => {
        const children = Number(childrenInput.value);
        const qng = qngInput.value === '1';

        switch (programInput.value) {
            case '296': return 100000;
            case '298': return qng ? 150000 : 100000;
            case '300': return qng
                ? (children >= 5 ? 270000 : (children >= 3 ? 250000 : 220000))
                : (children >= 5 ? 220000 : (children >= 3 ? 200000 : 170000));
            case '308': return children >= 3 ? 180000 : (children === 2 ? 160000 : 140000);
            default: return 0;
        }
    };

    const updateAmountLimit = () => {
        const eligibleAmount = programInput.value === '308' ? Math.min(loanAmount(), purchasePrice()) : loanAmount();
        const limit = eligibleAmount > 0 ? Math.min(maximumAmount(), eligibleAmount) : maximumAmount();
        const formatter = AutoNumeric.getAutoNumericElement(amountInput);
        amountInput.dataset.maxAmount = String(limit);
        limitText.textContent = form.dataset.limitTemplate.replace('__AMOUNT__', currencyNumber.format(limit));
        if (formatter) {
            formatter.update({ maximumValue: String(limit) });
            if (Number(formatter.getNumber()) > limit) formatter.set(limit);
        } else if (Number(amountInput.value) > limit) {
            amountInput.value = String(limit);
        }
        amountInput.setCustomValidity('');
    };

    const updateGraceOptions = () => {
        const term = Number(termInput.value);
        const maximum = term <= 10 ? 2 : (term <= 25 ? 3 : 5);
        const selected = Number(graceInput.value) || 1;
        graceInput.replaceChildren(...Array.from({ length: maximum }, (_, index) => {
            const year = index + 1;
            const option = new Option(String(year), String(year));
            option.selected = year === Math.min(selected, maximum);
            return option;
        }));
    };

    const updateProgram = (resetDefaults = false) => {
        const program = programInput.value;
        const option = programInput.selectedOptions[0];
        if (resetDefaults) {
            childrenInput.value = '1';
            qngInput.value = '0';
        }
        title.textContent = option.textContent;
        form.querySelector('[data-kfw-children-group]').hidden = !['300', '308'].includes(program);
        form.querySelector('[data-kfw-qng-group]').hidden = !['298', '300'].includes(program);
        termInput.min = program === '308' ? '7' : '4';
        termInput.max = '35';
        if (Number(termInput.value) < Number(termInput.min)) termInput.value = termInput.min;
        if (Number(termInput.value) > 35) termInput.value = '35';
        document.querySelectorAll('[data-kfw-program-panel], [data-kfw-program-note]').forEach((panel) => {
            panel.hidden = panel.dataset.kfwProgramPanel !== program && panel.dataset.kfwProgramNote !== program;
        });
        if (results) results.hidden = results.dataset.program !== program;

        updateAmountLimit();
        if (resetDefaults) {
            const amount = Math.min(Number(option.dataset.defaultAmount), Number(amountInput.dataset.maxAmount));
            const formatter = AutoNumeric.getAutoNumericElement(amountInput);
            if (formatter) formatter.set(amount);
            else amountInput.value = String(amount);
            interestInput.value = option.dataset.defaultInterest;
            termInput.value = option.dataset.defaultTerm;
            graceInput.value = option.dataset.defaultGrace;
        }
        updateGraceOptions();
    };

    programInput.addEventListener('change', () => updateProgram(true));
    childrenInput.addEventListener('change', updateAmountLimit);
    qngInput.addEventListener('change', updateAmountLimit);
    loanAmountInput?.addEventListener('change', updateAmountLimit);
    useEquityInput?.addEventListener('change', () => {
        updateAmountLimit();
        if (results) results.hidden = true;
    });
    form.addEventListener('kfw-equity-updated', () => {
        if (equityGroup) equityGroup.hidden = Number(form.dataset.equityAmount) <= 0;
        if (equityLabel) equityLabel.textContent = equityLabel.dataset.template.replace('__AMOUNT__', currencyNumber.format(Number(form.dataset.equityAmount)));
        updateAmountLimit();
        if (results) results.hidden = true;
    });
    termInput.addEventListener('change', updateGraceOptions);
    termInput.addEventListener('input', updateGraceOptions);
    updateProgram();
    let hasTrackedStart = false;
    const trackStart = () => {
        if (hasTrackedStart) return;
        hasTrackedStart = true;
        trackCalculatorStarted(form.dataset.analyticsCalculator || 'kfw_comparison');
    };
    ['input', 'change', 'submit'].forEach((eventName) => form.addEventListener(eventName, trackStart));
};

document.addEventListener('DOMContentLoaded', initKfwComparison);

const initLbankComparison = () => {
    const form = document.querySelector('[data-lbank-form]');
    const guestProgram = document.querySelector('[data-lbank-guest-program]');
    if (!form && guestProgram) {
        guestProgram.addEventListener('change', () => {
            document.querySelectorAll('[data-lbank-guest-panel]').forEach((panel) => {
                panel.hidden = panel.dataset.lbankGuestPanel !== guestProgram.value;
            });
        });
        return;
    }
    if (!form) return;

    initMoneyInputs(form);

    const program = form.querySelector('[data-lbank-program]');
    const units = form.querySelector('[data-lbank-units]');
    const qng = form.querySelector('[data-lbank-qng]');
    const variant = form.querySelector('[data-lbank-variant]');
    const term = form.querySelector('[data-lbank-term]');
    const grace = form.querySelector('[data-lbank-grace]');
    const amount = form.querySelector('[data-lbank-amount]');
    const totalLoan = form.querySelector('[data-lbank-loan-amount]');
    const useEquity = form.querySelector('[data-lbank-use-equity]');
    const limit = form.querySelector('[data-lbank-limit]');
    const note = form.querySelector('[data-lbank-program-note]');
    const results = document.querySelector('[data-lbank-results]');
    const terms = {
        z15: [25],
        family: [25, 35],
        combined: [10, 20, 30, 35],
        rental_bw: [20, 25, 30, 35],
        rental_lbank: [25, 35],
    };
    const notes = JSON.parse(form.dataset.programNotes);
    const confirmations = JSON.parse(form.dataset.programConfirmations);
    const confirmation = form.querySelector('[data-lbank-confirmation]');
    const eligibility = form.querySelector('#lbankEligibility');
    const format = new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 0 });
    const setOptions = (select, values, selected) => {
        select.replaceChildren(...values.map((value) => new Option(String(value), String(value), false, value === selected)));
    };
    const updateLimit = () => {
        const count = Number(units.value) || 1;
        let maximum = null;
        if (program.value === 'family') maximum = 100000;
        if (program.value === 'combined') maximum = 200000 * count;
        if (program.value === 'rental_lbank') maximum = (variant.value === '298' && qng.value === '1' ? 150000 : 100000) * count;
        const loan = totalLoan
            ? moneyInputValue(totalLoan)
            : Math.max(0, Number(form.dataset.baseLoanAmount) - (useEquity?.checked ? Number(form.dataset.equityAmount) : 0));
        const applied = Math.min(maximum ?? loan, loan);
        limit.textContent = maximum === null
            ? form.dataset.noFixedMaximum
            : form.dataset.maximumAmount.replace('__AMOUNT__', format.format(applied));
        const formatter = AutoNumeric.getAutoNumericElement(amount);
        if (formatter) {
            formatter.update({ maximumValue: String(Math.max(1, applied)) });
            if (Number(formatter.getNumber()) > applied) formatter.set(applied);
        } else if (moneyInputValue(amount) > applied) {
            amount.value = String(applied);
        }
    };
    const updateGrace = (selected = Number(grace.value || grace.dataset.selected)) => {
        const value = program.value;
        const years = value === 'z15' || value === 'rental_lbank'
            ? [2]
            : Array.from({ length: value === 'rental_bw' ? 3 : Math.min(5, Number(term.value) - 1) }, (_, index) => index + (value === 'rental_bw' ? 0 : 1));
        setOptions(grace, years, years.includes(selected) ? selected : years[0]);
    };
    const updateProgram = (reset = false) => {
        const value = program.value;
        form.querySelector('[data-lbank-units-group]').hidden = ['z15', 'family'].includes(value);
        if (['z15', 'family'].includes(value)) units.value = '1';
        units.max = value === 'combined' ? '3' : '100';
        form.querySelector('[data-lbank-qng-group]').hidden = value !== 'rental_lbank';
        form.querySelector('[data-lbank-variant-group]').hidden = value !== 'rental_lbank';
        if (value === 'rental_lbank' && variant.value === '296') qng.value = '0';
        form.querySelector('[data-lbank-base-group]').hidden = value !== 'combined';
        form.querySelector('[data-lbank-term-group]').hidden = value === 'z15';
        const allowedTerms = terms[value];
        const selectedTerm = reset ? 25 : Number(term.value || term.dataset.selected);
        setOptions(term, allowedTerms, allowedTerms.includes(selectedTerm) ? selectedTerm : allowedTerms[0]);
        updateGrace(reset ? 2 : Number(grace.dataset.selected));
        note.textContent = notes[value];
        confirmation.textContent = confirmations[value];
        if (reset) eligibility.checked = false;
        form.querySelectorAll('[data-lbank-program-panel]').forEach((panel) => {
            panel.hidden = panel.dataset.lbankProgramPanel !== value;
        });
        if (results) results.hidden = results.dataset.program !== value;
        updateLimit();
    };

    program.addEventListener('change', () => updateProgram(true));
    term.addEventListener('change', () => updateGrace());
    [units, qng, variant, totalLoan, useEquity].filter(Boolean).forEach((input) => input.addEventListener('change', updateLimit));
    form.addEventListener('lbank-equity-updated', () => {
        const label = form.querySelector('[data-lbank-equity-label]');
        const group = form.querySelector('[data-lbank-equity-group]');
        if (group) group.hidden = Number(form.dataset.equityAmount) <= 0;
        if (label) label.textContent = label.dataset.template.replace('__AMOUNT__', format.format(Number(form.dataset.equityAmount)));
        updateLimit();
        if (results) results.hidden = true;
    });
    variant.addEventListener('change', () => {
        if (variant.value === '296') qng.value = '0';
        updateLimit();
    });
    updateProgram();
    if (window.location.hash === '#lbank-results') results?.scrollIntoView({ block: 'start' });
    ['input', 'change'].forEach((eventName) => form.addEventListener(eventName, () => {
        if (results) results.hidden = true;
    }));
    let started = false;
    ['input', 'change', 'submit'].forEach((eventName) => form.addEventListener(eventName, () => {
        if (started) return;
        started = true;
        trackCalculatorStarted(form.dataset.analyticsCalculator);
    }));
};

document.addEventListener('DOMContentLoaded', initLbankComparison);

document.addEventListener('DOMContentLoaded', () => {
    initCookieConsent();
    initAnalyticsTracking().finally(initInvestmentFunnelHandoff);
    mobileSidebarToggle();
    initMobileNavSubmenus();
    professionalSettingsToggle();
    ownerEquityCardToggle();
    familyTransferInfoToggle();
    initInfoDetails();
    initTooltips();
    scrollToCalculationResults();
    initLogoutTrigger();
    initDonationCta();
    initCashflowPwaPrompt();
    guestAuthCtaPulse();
    initKnowledgePostForm();
    initKnowledgeMarkdownEditors();
    initKnowledgeImageUpload();
    initKnowledgeTeaserToggles();
    initKnowledgeSearch();
    initKnowledgeSliders();
    document.querySelectorAll('[data-video-card]').forEach((card) => {
        card.querySelector('[data-video-play]')?.addEventListener('click', () => {
            const id = card.dataset.videoId;
            if (!/^[A-Za-z0-9_-]{11}$/.test(id)) {
                return;
            }

            const iframe = document.createElement('iframe');
            iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`;
            iframe.title = card.querySelector('h3')?.textContent || '';
            iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            card.querySelector('[data-video-media]').replaceChildren(iframe);
        });
    });
    initShareButton();
    initFinancingEmbedCopy();
    initWidgetConfigurators();
});

//TODO  Obfuscation ( UglifyJS oder Terser )
